import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Charger le panier du localStorage
    const savedCart = localStorage.getItem('ninacare_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      const productsUrl = selectedCategory === 'all' 
        ? `${API}/shop/products`
        : `${API}/shop/products?category=${selectedCategory}`;
      
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(productsUrl),
        axios.get(`${API}/shop/categories`)
      ]);
      
      setProducts(productsRes.data);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Erreur chargement boutique:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      newCart = [...cart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('ninacare_cart', JSON.stringify(newCart));
    toast.success('Ajouté au panier !');
  };

  const updateQuantity = (productId, delta) => {
    const newCart = cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    
    setCart(newCart);
    localStorage.setItem('ninacare_cart', JSON.stringify(newCart));
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.product_id !== productId);
    setCart(newCart);
    localStorage.setItem('ninacare_cart', JSON.stringify(newCart));
    toast.success('Retiré du panier');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }
    // Rediriger vers la page de paiement
    window.location.href = '/shop/checkout';
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.1) 0%, rgba(201, 169, 97, 0.05) 100%)',
        padding: '80px 20px 60px'
      }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 className="hero-large" style={{ marginBottom: '16px' }}>Boutique</h1>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Produits de beauté naturels & Cartes cadeaux
              </p>
            </div>
            
            {/* Bouton Panier */}
            <button
              onClick={() => setShowCart(!showCart)}
              style={{
                position: 'relative',
                padding: '12px 24px',
                background: '#C9A961',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 600
              }}
            >
              <ShoppingCart size={20} />
              Panier ({cart.length})
              {cart.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#DC2626',
                  color: 'white',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 700
                }}>
                  {cart.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: '40px 20px' }}>
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Filtres */}
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '40px'
          }}>
            <button
              onClick={() => setSelectedCategory('all')}
              style={{
                padding: '10px 24px',
                background: selectedCategory === 'all' ? '#C9A961' : 'white',
                color: selectedCategory === 'all' ? 'white' : 'var(--text-primary)',
                border: selectedCategory === 'all' ? 'none' : '1px solid var(--border-medium)',
                borderRadius: '24px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Tous les produits
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '10px 24px',
                  background: selectedCategory === cat ? '#C9A961' : 'white',
                  color: selectedCategory === cat ? 'white' : 'var(--text-primary)',
                  border: selectedCategory === cat ? 'none' : '1px solid var(--border-medium)',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grille de produits */}
          {loading ? (
            <p style={{ textAlign: 'center', padding: '60px' }}>Chargement...</p>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Aucun produit disponible pour le moment.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '32px'
            }}>
              {products.map(product => (
                <div
                  key={product.id}
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {product.image && (
                    <div style={{ height: '240px', overflow: 'hidden' }}>
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  
                  <div style={{ padding: '24px' }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#C9A961',
                      fontWeight: 600,
                      marginBottom: '8px'
                    }}>
                      {product.category}
                    </div>
                    
                    <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                      {product.name}
                    </h3>
                    
                    <p className="body-regular" style={{
                      color: 'var(--text-secondary)',
                      marginBottom: '16px',
                      minHeight: '60px'
                    }}>
                      {product.description}
                    </p>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span className="heading-2" style={{ color: '#C9A961' }}>
                        {product.price.toFixed(2)} €
                      </span>
                      
                      <button
                        onClick={() => addToCart(product)}
                        className="btn-primary"
                        style={{
                          background: '#C9A961',
                          padding: '10px 20px',
                          fontSize: '14px'
                        }}
                      >
                        Ajouter au panier
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Panier flottant */}
      {showCart && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '450px',
          height: '100vh',
          background: 'white',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '24px',
            borderBottom: '2px solid var(--border-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 className="heading-2">Votre panier</h2>
            <button
              onClick={() => setShowCart(false)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            {cart.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px 0' }}>
                Votre panier est vide
              </p>
            ) : (
              cart.map(item => (
                <div
                  key={item.product_id}
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '16px',
                    padding: '16px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px'
                  }}
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                  
                  <div style={{ flex: 1 }}>
                    <h4 className="body-regular" style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {item.name}
                    </h4>
                    <p style={{ color: '#C9A961', fontWeight: 600 }}>
                      {item.price.toFixed(2)} €
                    </p>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={() => updateQuantity(item.product_id, -1)}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          border: '1px solid var(--border-medium)',
                          cursor: 'pointer'
                        }}
                      >
                        <Minus size={14} />
                      </button>
                      
                      <span style={{ minWidth: '30px', textAlign: 'center' }}>
                        {item.quantity}
                      </span>
                      
                      <button
                        onClick={() => updateQuantity(item.product_id, 1)}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          border: '1px solid var(--border-medium)',
                          cursor: 'pointer'
                        }}
                      >
                        <Plus size={14} />
                      </button>
                      
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          border: '1px solid #dc2626',
                          color: '#dc2626',
                          cursor: 'pointer',
                          marginLeft: 'auto'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div style={{
            padding: '24px',
            borderTop: '2px solid var(--border-light)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px',
              fontSize: '20px',
              fontWeight: 700
            }}>
              <span>Total</span>
              <span style={{ color: '#C9A961' }}>{getCartTotal().toFixed(2)} €</span>
            </div>
            
            <button
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="btn-primary"
              style={{
                width: '100%',
                background: cart.length === 0 ? '#ccc' : '#C9A961',
                cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <CreditCard size={20} />
              Passer la commande
            </button>
          </div>
        </div>
      )}
      
      {showCart && (
        <div
          onClick={() => setShowCart(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
        />
      )}
    </div>
  );
};

export default Shop;
